import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { ArrowRight } from "lucide-react";

const ODISHA_CITIES = [
  "All of Odisha",
  "Bhubaneswar",
  "Rourkela",
  "Bondamunda",
  "Cuttack",
  "Berhampur",
  "Puri",
  "Sambalpur",
  "—— DISTRICTS ——",
  "Angul",
  "Balangir",
  "Balasore (Baleshwar)",
  "Bargarh",
  "Bhadrak",
  "Boudh",
  "Deogarh (Debagarh)",
  "Dhenkanal",
  "Gajapati",
  "Ganjam",
  "Jagatsinghpur",
  "Jajpur",
  "Jharsuguda",
  "Kalahandi",
  "Kandhamal",
  "Kendrapara",
  "Keonjhar (Kendujhar)",
  "Khordha",
  "Koraput",
  "Malkangiri",
  "Mayurbhanj",
  "Nabarangpur",
  "Nayagarh",
  "Nuapada",
  "Rayagada",
  "Subarnapur (Sonepur)",
  "Sundargarh",
  "Outside Odisha",
];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    city: "", profession: "", height_cm: "", weight_kg: "",
    running_history: "", future_goals: ""
  });

  if (!user) { navigate("/login"); return null; }
  if (user.onboarding_complete) { navigate("/dashboard"); return null; }

  const next = () => {
    if (step === 1 && !f.city) { toast.error("Please pick your city"); return; }
    setStep(step + 1);
  };

  const submit = async () => {
    if (!f.future_goals.trim()) { toast.error("Tell us your running goal — even one line."); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/auth/onboarding", {
        city: f.city,
        state: "Odisha",
        profession: f.profession,
        height_cm: f.height_cm ? Number(f.height_cm) : null,
        weight_kg: f.weight_kg ? Number(f.weight_kg) : null,
        running_history: f.running_history,
        future_goals: f.future_goals,
      });
      setUser(data);
      localStorage.setItem("oscr_show_welcome", "1");
      toast.success("Welcome to the pack, " + (data.name || "runner") + "!");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Could not save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid lg:grid-cols-2 bg-background" data-testid="onboarding-page">
      <SEO title="Tell Us About You" path="/onboarding" description="Quick onboarding for new OSCR runners — city, profession, running history, goals." />
      {/* Image side */}
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/13067841/pexels-photo-13067841.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=940" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950 via-zinc-950/60 to-transparent" />
        <div className="relative h-full flex items-end p-12">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-orange-500 uppercase">STEP {step} OF 3 · ONBOARDING</p>
            <h2 className="font-display text-5xl text-white mt-3 max-w-md leading-none">
              Before your first mile,<br /><span className="text-orange-500">let us know you.</span>
            </h2>
            <p className="text-zinc-300 mt-4 max-w-md text-sm">
              We use these details to personalise training tips, recommend the right challenges,
              and connect you with runners from your city.
            </p>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-8 sm:p-16">
        <div className="w-full max-w-md">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <span key={s} className={`h-1 flex-1 ${s <= step ? "bg-orange-500" : "bg-zinc-800"}`} />
            ))}
          </div>
          <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">Step {step} of 3</p>
          <h1 className="font-display text-3xl text-white mt-2">
            {step === 1 && "Where are you running from?"}
            {step === 2 && "Your physical baseline"}
            {step === 3 && "Your story & goals"}
          </h1>

          <div className="mt-8 space-y-4">
            {step === 1 && (
              <>
                <div>
                  <Label className="font-display tracking-widest text-xs text-zinc-400">CITY *</Label>
                  <Select value={f.city} onValueChange={(v) => setF({ ...f, city: v })}>
                    <SelectTrigger data-testid="onb-city" className="mt-2 rounded-none bg-zinc-900 border-zinc-800 text-white focus:ring-orange-600">
                      <SelectValue placeholder="Pick your city" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none max-h-72">
                      {ODISHA_CITIES.map((c) => (
                        c.startsWith("——") ? (
                          <div key={c} className="px-2 py-1.5 font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase pointer-events-none">
                            {c}
                          </div>
                        ) : (
                          <SelectItem key={c} value={c} className="rounded-none focus:bg-orange-600 focus:text-white">{c}</SelectItem>
                        )
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-display tracking-widest text-xs text-zinc-400">PROFESSION / WORK</Label>
                  <Input data-testid="onb-profession" value={f.profession} onChange={(e) => setF({ ...f, profession: e.target.value })}
                    placeholder="e.g., Steel engineer, doctor, student, business owner..."
                    className="mt-2 rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600" />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="font-display tracking-widest text-xs text-zinc-400">HEIGHT (CM)</Label>
                    <Input data-testid="onb-height" type="number" value={f.height_cm}
                      onChange={(e) => setF({ ...f, height_cm: e.target.value })}
                      placeholder="e.g. 172"
                      className="mt-2 rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600" />
                  </div>
                  <div>
                    <Label className="font-display tracking-widest text-xs text-zinc-400">WEIGHT (KG)</Label>
                    <Input data-testid="onb-weight" type="number" step="0.1" value={f.weight_kg}
                      onChange={(e) => setF({ ...f, weight_kg: e.target.value })}
                      placeholder="e.g. 68"
                      className="mt-2 rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600" />
                  </div>
                </div>
                <p className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
                  Used only to personalise pacing & nutrition tips. Never shown publicly.
                </p>
              </>
            )}

            {step === 3 && (
              <>
                <div>
                  <Label className="font-display tracking-widest text-xs text-zinc-400">RUNNING HISTORY</Label>
                  <Textarea data-testid="onb-history" value={f.running_history}
                    onChange={(e) => setF({ ...f, running_history: e.target.value })}
                    placeholder="Total runner? Just starting? Done any races (5K, 10K, half, full marathon)? Any best times?"
                    className="mt-2 rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600 min-h-[100px]" />
                </div>
                <div>
                  <Label className="font-display tracking-widest text-xs text-zinc-400">FUTURE GOALS *</Label>
                  <Textarea data-testid="onb-goals" value={f.future_goals}
                    onChange={(e) => setF({ ...f, future_goals: e.target.value })}
                    placeholder="What do you want to achieve? First 5K? Sub-2hr half? Boston qualifier? An ultra?"
                    className="mt-2 rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600 min-h-[100px]" />
                </div>
              </>
            )}
          </div>

          <div className="mt-8 flex items-center justify-between gap-3">
            {step > 1 ? (
              <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="onb-back"
                className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider">
                BACK
              </Button>
            ) : <span />}
            {step < 3 ? (
              <Button onClick={next} data-testid="onb-next"
                className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider px-6">
                NEXT <ArrowRight size={16} className="ml-2" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={busy} data-testid="onb-submit"
                className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider px-6">
                {busy ? "SAVING…" : "FINISH & ENTER"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
